import { getApiUrl } from '@/lib/api-config';
import { apiUpload } from '@/lib/api-request';
import { MAX_FILE_BYTES } from '@/lib/file-upload';
import type { BreadcrumbItem, CreateFolderData, CreateUrlData, DriveFolderNode } from '../types';

export const DRIVE_BUCKET = 'drive-attachments';

/** A row as the API returns it (dates are ISO strings). */
export type DriveRow = Record<string, any> & { id: string; name: string; type: 'file' | 'folder' | 'url' };

async function request<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const response = await fetch(getApiUrl(path), {
    method: init.method || 'GET',
    headers: init.body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || payload?.message || 'Request failed');
  return payload.data as T;
}

const itemsPath = (projectId: string, parentId: string | null) =>
  `api/drive/items?project_id=${encodeURIComponent(projectId)}${parentId ? `&parent_id=${encodeURIComponent(parentId)}` : ''}`;

/**
 * Every Drive read and write goes through the backend API (company, project, permissions, unique names, folder rules): the browser
 * never talks to Storage -- file bytes are uploaded to the API and files are opened through the API's content route (lib/file-urls.ts).
 */
export const driveApi = {
  /** One folder: its items and the breadcrumb trail, in one request. */
  listFolder: (projectId: string, parentId: string | null) =>
    request<{ items: DriveRow[]; breadcrumbs: BreadcrumbItem[] }>(itemsPath(projectId, parentId)),

  /** Every folder of the project (the Move destinations). */
  listFolders: (projectId: string) =>
    request<DriveFolderNode[]>(`api/drive/folders?project_id=${encodeURIComponent(projectId)}`),

  createFolder: (data: CreateFolderData) =>
    request<DriveRow>('api/entities/drive_attachment', { method: 'POST', body: { ...data, type: 'folder' } }),

  createUrl: (data: CreateUrlData) =>
    request<DriveRow>('api/entities/drive_attachment', { method: 'POST', body: { ...data, type: 'url' } }),

  update: (id: string, changes: { name?: string; url?: string }) =>
    request<DriveRow>(`api/entities/drive_attachment/${id}`, { method: 'PUT', body: changes }),

  move: (ids: string[], parentId: string | null) =>
    request<{ moved: DriveRow[]; skipped: number }>('api/drive/move', { method: 'POST', body: { ids, parent_id: parentId } }),

  remove: (ids: string[]) => request<{ deleted: number }>('api/drive/delete', { method: 'POST', body: { ids } }),

  /**
   * Upload one file: the server stores the bytes and creates the record (and picks the final name: "name (2)" when taken). When
   * the record cannot be created the server removes the object again, so a failed upload never leaves an orphan file behind.
   */
  uploadFile(file: File, ctx: { projectId: string; parentId: string | null }): Promise<DriveRow> {
    if (file.size > MAX_FILE_BYTES) return Promise.reject(new Error('File exceeds the 20MB limit'));
    const form = new FormData();
    form.append('project_id', ctx.projectId);
    if (ctx.parentId) form.append('parent_id', ctx.parentId);
    form.append('file_name', file.name);
    form.append('file', file, file.name);
    return apiUpload<DriveRow>('api/drive/upload', form);
  },
};
